import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { Events } from "ionic-angular";
import moment, { Moment } from "moment";
import { Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PatientHopeDataResponse } from "../../../../models/patient-chart/patientHopeDataResponse";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";

interface MessagePart {
  name: string;
  dueDate: Moment;
}

@Component({
  selector: "huv-notification",
  templateUrl: "huv-notification.html",
})
export class HuvNotification implements OnInit, OnChanges {
  // Output events
  @Input() patientChartId: any;
  @Input() type: "briefBar" | "" = "";
  @Output() closeEvent = new EventEmitter<any>();
  @Output() showEvent = new EventEmitter<any>();

  private hopeData: PatientHopeDataResponse;
  private subscriptions = new Subscription();
  public message = "";
  private labels = new Map<string, string>();
  private reloadHuvNotificationHandler: () => void;

  constructor(
    private dataService: AssessmentDataService,
    private stateData: StateManagementService,
    private events: Events
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.stateData.showHOPEToggle
        .pipe(takeUntil(this.stateData.logout$))
        .subscribe((hopeToggle) => {
          if (hopeToggle) {
            this.retrieveData();
          }
        })
    );

    this.reloadHuvNotificationHandler = () => {
      this.message = "";
      this.closeEvent.emit();
      this.retrieveData();
    };
    this.events.subscribe(
      "reloadHuvNotificationEvent",
      this.reloadHuvNotificationHandler
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.patientChartId && !changes.patientChartId.firstChange) {
      this.message = "";
      this.closeEvent.emit();
      this.retrieveData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.reloadHuvNotificationHandler) {
      this.events.unsubscribe(
        "reloadHuvNotificationEvent",
        this.reloadHuvNotificationHandler
      );
      this.reloadHuvNotificationHandler = undefined;
    }
  }

  private loadData(): void {
    const requiredAssessments: MessagePart[] = [];

    for (const key in this.hopeData) {
      if (this.hopeData[key]?.required) {
        requiredAssessments.push({
          name: key,
          // The backend stamps the intended calendar date +0000 — format in
          // UTC to match the calendar legend; .local() rolls it back a day
          dueDate: moment.utc(
            this.hopeData[key].dueDate,
            "YYYYMMDDTHHmmssSSS+0000"
          ),
        });
      }
    }
    requiredAssessments.sort((a, b) => a.dueDate.diff(b.dueDate));
    if (requiredAssessments.length > 0) {
      if (this.type === "briefBar") {
        this.labels.set("admission", "INV");
        this.labels.set("huv1", "HUV-1");
        this.labels.set("huv2", "HUV-2");
        this.labels.set("admissionSfv", "SFV");
        this.labels.set("huv1Sfv", "SFV");
        this.labels.set("huv2Sfv", "SFV");
      } else {
        this.labels.set("admission", "Patient needs an INV");
        this.labels.set("huv1", "Patient needs a HOPE Update Visit 1");
        this.labels.set("huv2", "Patient needs a HOPE Update Visit 2");
        this.labels.set(
          "admissionSfv",
          "Patient needs a Symptom Follow-up Visit"
        );
        this.labels.set("huv1Sfv", "Patient needs a Symptom Follow-up Visit");
        this.labels.set("huv2Sfv", "Patient needs a Symptom Follow-up Visit");
      }
    }
    for (let i = 0; i < requiredAssessments.length; i++) {
      if (i > 0) {
        this.message += "; ";
      }
      this.message += `${this.labels.get(
        requiredAssessments[i].name
      )} by ${moment(requiredAssessments[i].dueDate).format("MM/DD/YYYY")}`;
      if (this.type == "briefBar") {
        break;
      }
    }
    if (this.message.length > 0) {
      this.showEvent.emit();
    }
  }

  private retrieveData(): void {
    this.subscriptions.add(
      this.dataService
        .getPatientHopeDatasByPatientChartId(this.patientChartId, "hope")
        .pipe(takeUntil(this.stateData.logout$))
        .subscribe((response: PatientHopeDataResponse) => {
          if (response) {
            this.hopeData = response;
            this.loadData();
          }
        })
    );
  }

  public handleClose(): void {
    this.closeEvent.emit();
  }
}



// WEBPACK FOOTER //
// ./src/pages/assessment/visits/huv-notification/huv-notification.ts